import { DecimalPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { format, getYear } from 'date-fns';
import { ITrendsDataPoint } from '../models/IMarketTrendsGraphSet';
import { TrendDataTypeEnum } from '../models/ITrendsTableData';
import { MarketTrendsChartConfiguration, TrendsChartStyles, TrendsChartStylesEnum } from '../models/MarketTrendsChartConfiguration';
import { MarketTrendsService } from './market-trends.service';
import { ThemeService } from './theme.service';

@Injectable({
  providedIn: 'root'
})
export class ChartService {
  constructor(private decimalPipe: DecimalPipe, private marketTrendsService: MarketTrendsService) {}

  monthIncluded(chartMonth: string, selectedMonth: string) {
    return chartMonth.includes(selectedMonth);
  }

  monthVars(month: string, currentMonth: string) {
    const chartMonth = month;
    const selectedMonth = currentMonth.substring(0, 3) ?? '';
    return { chartMonth, selectedMonth };
  }

  getSeriesDisplayName(dataType: string): string {
    return TrendDataTypeEnum[dataType as keyof typeof TrendDataTypeEnum] || dataType;
  }

  fetchChartConfig(size: TrendsChartStyles[], customChart = false) {
    return this.marketTrendsService.getTrendsConfig(size, customChart);
  }

  formatDates(value: ITrendsDataPoint[]) {
    let year = 0;
    return value.map(v => {
      const d = new Date(v.xData);
      const month = format(d, 'MMMM').substring(0, 3);
      year = getYear(d);
      v.xData = `${month} ${year}`;
      return v;
    });
  }

  fetchEchartsChartConfig(
    chartTypes: TrendsChartStyles[],
    isCustom: boolean,
    chartData: ITrendsDataPoint[][],
    chart: MarketTrendsChartConfiguration,
    currentMonth: string,
    isMobile: boolean,
    showColumnMarkers: boolean,
    themeService: ThemeService,
    decimalPipe: DecimalPipe
  ): any {
    switch (chart.shortHand) {
      case 'amsp':
        return this.getAverageMedianSalesPriceConfig(
          chartTypes,
          isCustom,
          chartData,
          chart,
          currentMonth,
          isMobile,
          showColumnMarkers,
          themeService,
          decimalPipe
        );
      case 'splp':
        return this.getSalesToListPriceConfig(
          chartTypes,
          isCustom,
          chartData,
          chart,
          currentMonth,
          isMobile,
          showColumnMarkers,
          themeService,
          decimalPipe
        );
      case 'nbrsoldar':
        return this.getPropertiesSoldAbsorptionRateConfig(
          chartTypes,
          isCustom,
          chartData,
          chart,
          currentMonth,
          isMobile,
          showColumnMarkers,
          themeService,
          decimalPipe
        );
      case 'dom':
        return this.getDaysOnMarketConfig(
          chartTypes,
          isCustom,
          chartData,
          chart,
          currentMonth,
          isMobile,
          showColumnMarkers,
          themeService,
          decimalPipe
        );
      case 'spsqft':
        return this.getSalesPricePerSqFtConfig(
          chartTypes,
          isCustom,
          chartData,
          chart,
          currentMonth,
          isMobile,
          showColumnMarkers,
          themeService,
          decimalPipe
        );
      case 'aimsi':
        return this.getInventoryMsiConfig(
          chartTypes,
          isCustom,
          chartData,
          chart,
          currentMonth,
          isMobile,
          showColumnMarkers,
          themeService,
          decimalPipe
        );
      case 'nl':
        return this.getNewListingsConfig(
          chartTypes,
          isCustom,
          chartData,
          chart,
          currentMonth,
          isMobile,
          showColumnMarkers,
          themeService,
          decimalPipe
        );
      case 'overview':
        return this.getOverviewConfig(
          chartTypes,
          isCustom,
          chartData,
          chart,
          currentMonth,
          isMobile,
          showColumnMarkers,
          themeService,
          decimalPipe
        );
      case 'custom':
        return this.getCustomConfig(
          chartTypes,
          isCustom,
          chartData,
          chart,
          currentMonth,
          isMobile,
          showColumnMarkers,
          themeService,
          decimalPipe
        );
      default:
        return this.buildEchartsConfig(
          chartTypes,
          isCustom,
          chartData,
          chart,
          currentMonth,
          isMobile,
          showColumnMarkers,
          themeService,
          decimalPipe
        );
    }
  }

  private getAverageMedianSalesPriceConfig(
    chartTypes: TrendsChartStyles[],
    isCustom: boolean,
    chartData: ITrendsDataPoint[][],
    chart: MarketTrendsChartConfiguration,
    currentMonth: string,
    isMobile: boolean,
    showColumnMarkers: boolean,
    themeService: ThemeService,
    decimalPipe: DecimalPipe
  ): any {
    return this.buildEchartsConfig(
      chartTypes,
      isCustom,
      chartData,
      chart,
      currentMonth,
      isMobile,
      showColumnMarkers,
      themeService,
      decimalPipe
    );
  }

  private getSalesToListPriceConfig(
    chartTypes: TrendsChartStyles[],
    isCustom: boolean,
    chartData: ITrendsDataPoint[][],
    chart: MarketTrendsChartConfiguration,
    currentMonth: string,
    isMobile: boolean,
    showColumnMarkers: boolean,
    themeService: ThemeService,
    decimalPipe: DecimalPipe
  ): any {
    return this.buildEchartsConfig(
      chartTypes,
      isCustom,
      chartData,
      chart,
      currentMonth,
      isMobile,
      showColumnMarkers,
      themeService,
      decimalPipe
    );
  }

  private getPropertiesSoldAbsorptionRateConfig(
    chartTypes: TrendsChartStyles[],
    isCustom: boolean,
    chartData: ITrendsDataPoint[][],
    chart: MarketTrendsChartConfiguration,
    currentMonth: string,
    isMobile: boolean,
    showColumnMarkers: boolean,
    themeService: ThemeService,
    decimalPipe: DecimalPipe
  ): any {
    const echartsConfig = this.buildEchartsConfig(
      chartTypes,
      isCustom,
      chartData,
      chart,
      currentMonth,
      isMobile,
      showColumnMarkers,
      themeService,
      decimalPipe
    );

    if (echartsConfig && echartsConfig.series) {
      echartsConfig.series.forEach((seriesConfig: any, idx: number) => {
        if (seriesConfig.type === 'bar') {
          // Show the number on every bar (including mobile), vertical/rotated like prod
          // (top-to-bottom): blue text on the light-blue bars, white text on the grey
          // current-month bar.
          seriesConfig.label = {
            show: true,
            position: 'insideTop',
            rotate: -90,
            offset: [6, 2],
            formatter: (params: any) => {
              const value = params.value;
              return value > 0 ? decimalPipe.transform(value) : '';
            },
            color: '#1F69FF',
            align: 'center',
            verticalAlign: 'middle',
            fontFamily: 'Roboto',
            fontSize: 11
          };

          // White label on the grey current-month bars (set per-point so it reliably overrides).
          seriesConfig.data = (seriesConfig.data || []).map((point: any, pointIdx: number) => {
            const { chartMonth, selectedMonth } = this.monthVars(chartData[idx][pointIdx]?.xData || '', currentMonth);
            if (this.monthIncluded(chartMonth, selectedMonth)) {
              const dataPoint = point && typeof point === 'object' ? { ...point } : { value: point };
              dataPoint.label = { color: '#fff' };
              return dataPoint;
            }
            return point;
          });
        } else if (seriesConfig.type === 'line') {
          // Remove the dots/markers on the Absorption Rate trend line.
          seriesConfig.showSymbol = false;
          seriesConfig.symbol = 'none';
          seriesConfig.data = (seriesConfig.data || []).map((point: any) =>
            point && typeof point === 'object' && 'value' in point ? point.value : point
          );
        }
      });
    }

    return echartsConfig;
  }

  private getDaysOnMarketConfig(
    chartTypes: TrendsChartStyles[],
    isCustom: boolean,
    chartData: ITrendsDataPoint[][],
    chart: MarketTrendsChartConfiguration,
    currentMonth: string,
    isMobile: boolean,
    showColumnMarkers: boolean,
    themeService: ThemeService,
    decimalPipe: DecimalPipe
  ): any {
    const echartsConfig = this.buildEchartsConfig(
      chartTypes,
      isCustom,
      chartData,
      chart,
      currentMonth,
      isMobile,
      showColumnMarkers,
      themeService,
      decimalPipe
    );

    if (echartsConfig && echartsConfig.series) {
      echartsConfig.series.forEach((seriesConfig: any, idx: number) => {
        if (seriesConfig.type === 'bar') {
          // Show the number on every bar (including mobile), vertical/rotated like prod
          // (top-to-bottom): blue text on the light-blue bars, white text on the grey
          // current-month bar.
          seriesConfig.label = {
            show: true,
            position: 'insideTop',
            rotate: -90,
            offset: [6, 2],
            formatter: (params: any) => {
              const value = params.value;
              return value > 0 ? decimalPipe.transform(value) : '';
            },
            color: '#1F69FF',
            align: 'center',
            verticalAlign: 'middle',
            fontFamily: 'Roboto',
            fontSize: 11
          };

          // White label on the grey current-month bars (set per-point so it reliably overrides).
          seriesConfig.data = (seriesConfig.data || []).map((point: any, pointIdx: number) => {
            const { chartMonth, selectedMonth } = this.monthVars(chartData[idx][pointIdx]?.xData || '', currentMonth);
            if (this.monthIncluded(chartMonth, selectedMonth)) {
              const dataPoint = point && typeof point === 'object' ? { ...point } : { value: point };
              dataPoint.label = { color: '#fff' };
              return dataPoint;
            }
            return point;
          });
        }
      });
    }

    return echartsConfig;
  }

  private getSalesPricePerSqFtConfig(
    chartTypes: TrendsChartStyles[],
    isCustom: boolean,
    chartData: ITrendsDataPoint[][],
    chart: MarketTrendsChartConfiguration,
    currentMonth: string,
    isMobile: boolean,
    showColumnMarkers: boolean,
    themeService: ThemeService,
    decimalPipe: DecimalPipe
  ): any {
    return this.buildEchartsConfig(
      chartTypes,
      isCustom,
      chartData,
      chart,
      currentMonth,
      isMobile,
      showColumnMarkers,
      themeService,
      decimalPipe
    );
  }

  private getInventoryMsiConfig(
    chartTypes: TrendsChartStyles[],
    isCustom: boolean,
    chartData: ITrendsDataPoint[][],
    chart: MarketTrendsChartConfiguration,
    currentMonth: string,
    isMobile: boolean,
    showColumnMarkers: boolean,
    themeService: ThemeService,
    decimalPipe: DecimalPipe
  ): any {
    const echartsConfig = this.buildEchartsConfig(
      chartTypes,
      isCustom,
      chartData,
      chart,
      currentMonth,
      isMobile,
      showColumnMarkers,
      themeService,
      decimalPipe
    );

    if (echartsConfig && echartsConfig.series) {
      echartsConfig.series.forEach((seriesConfig: any, idx: number) => {
        if (seriesConfig.type === 'bar') {
          // Show the number on every bar (including mobile), vertical/rotated like prod
          // (top-to-bottom): blue text on the light-blue bars, white text on the grey
          // current-month bar.
          seriesConfig.label = {
            show: true,
            position: 'insideTop',
            rotate: -90,
            offset: [6, 2],
            formatter: (params: any) => {
              const value = params.value;
              return value > 0 ? decimalPipe.transform(value) : '';
            },
            color: '#1F69FF',
            align: 'center',
            verticalAlign: 'middle',
            fontFamily: 'Roboto',
            fontSize: 11
          };

          // White label on the grey current-month bars (set per-point so it reliably overrides).
          seriesConfig.data = (seriesConfig.data || []).map((point: any, pointIdx: number) => {
            const { chartMonth, selectedMonth } = this.monthVars(chartData[idx][pointIdx]?.xData || '', currentMonth);
            if (this.monthIncluded(chartMonth, selectedMonth)) {
              const dataPoint = point && typeof point === 'object' ? { ...point } : { value: point };
              dataPoint.label = { color: '#fff' };
              return dataPoint;
            }
            return point;
          });
        } else if (seriesConfig.type === 'line') {
          // Remove the dots/markers on the MSI trend line.
          seriesConfig.showSymbol = false;
          seriesConfig.symbol = 'none';
          seriesConfig.data = (seriesConfig.data || []).map((point: any) =>
            point && typeof point === 'object' && 'value' in point ? point.value : point
          );
        }
      });
    }

    return echartsConfig;
  }

  private getNewListingsConfig(
    chartTypes: TrendsChartStyles[],
    isCustom: boolean,
    chartData: ITrendsDataPoint[][],
    chart: MarketTrendsChartConfiguration,
    currentMonth: string,
    isMobile: boolean,
    showColumnMarkers: boolean,
    themeService: ThemeService,
    decimalPipe: DecimalPipe
  ): any {
    const echartsConfig = this.buildEchartsConfig(
      chartTypes,
      isCustom,
      chartData,
      chart,
      currentMonth,
      isMobile,
      showColumnMarkers,
      themeService,
      decimalPipe
    );

    if (echartsConfig && echartsConfig.series) {
      echartsConfig.series.forEach((seriesConfig: any, idx: number) => {
        if (seriesConfig.type === 'bar') {
          // Show the number on every bar (including mobile), vertical/rotated like prod
          // (top-to-bottom): blue text on the light-blue bars, white text on the grey
          // current-month bar.
          seriesConfig.label = {
            show: true,
            position: 'insideTop',
            rotate: -90,
            offset: [6, 2],
            formatter: (params: any) => {
              const value = params.value;
              return value > 0 ? decimalPipe.transform(value) : '';
            },
            color: '#1F69FF',
            align: 'center',
            verticalAlign: 'middle',
            fontFamily: 'Roboto',
            fontSize: 11
          };

          // White label on the grey current-month bars (set per-point so it reliably overrides).
          seriesConfig.data = (seriesConfig.data || []).map((point: any, pointIdx: number) => {
            const { chartMonth, selectedMonth } = this.monthVars(chartData[idx][pointIdx]?.xData || '', currentMonth);
            if (this.monthIncluded(chartMonth, selectedMonth)) {
              const dataPoint = point && typeof point === 'object' ? { ...point } : { value: point };
              dataPoint.label = { color: '#fff' };
              return dataPoint;
            }
            return point;
          });
        }
      });
    }

    return echartsConfig;
  }

  private getOverviewConfig(
    chartTypes: TrendsChartStyles[],
    isCustom: boolean,
    chartData: ITrendsDataPoint[][],
    chart: MarketTrendsChartConfiguration,
    currentMonth: string,
    isMobile: boolean,
    showColumnMarkers: boolean,
    themeService: ThemeService,
    decimalPipe: DecimalPipe
  ): any {
    return this.buildEchartsConfig(
      chartTypes,
      isCustom,
      chartData,
      chart,
      currentMonth,
      isMobile,
      showColumnMarkers,
      themeService,
      decimalPipe
    );
  }

  private getCustomConfig(
    chartTypes: TrendsChartStyles[],
    isCustom: boolean,
    chartData: ITrendsDataPoint[][],
    chart: MarketTrendsChartConfiguration,
    currentMonth: string,
    isMobile: boolean,
    showColumnMarkers: boolean,
    themeService: ThemeService,
    decimalPipe: DecimalPipe
  ): any {
    return this.buildEchartsConfig(
      chartTypes,
      isCustom,
      chartData,
      chart,
      currentMonth,
      isMobile,
      showColumnMarkers,
      themeService,
      decimalPipe
    );
  }

  private buildEchartsConfig(
    chartTypes: TrendsChartStyles[],
    isCustom: boolean,
    chartData: ITrendsDataPoint[][],
    chart: MarketTrendsChartConfiguration,
    currentMonth: string,
    isMobile: boolean,
    showColumnMarkers: boolean,
    themeService: ThemeService,
    decimalPipe: DecimalPipe
  ): any {
    const config = this.marketTrendsService.getTrendsConfig(chartTypes, isCustom);
    const palette = config.palette || [];

    // Get CSS variable values
    const computedStyle = getComputedStyle(document.documentElement);
    const primaryColor = themeService.chartPrimary || '#1F69FF';
    const textColor = computedStyle.getPropertyValue('--font-color').trim() || '#727476';
    const isSir = themeService.isSIR();

    // Build series data
    const series = [];
    if (chartData && chartData.length > 0) {
      chartData.forEach((data, idx) => {
        const chartType = chart.chartTypes[idx];
        const seriesColor = palette[idx] || primaryColor;
        const isLineType = chartType === 'Spline' || chartType === 'Line';

        // Build per-point data objects so markers only show for the current month
        const seriesData = data.map((d, pointIdx) => {
          if (isLineType) {
            const { chartMonth, selectedMonth } = this.monthVars(d.xData || '', currentMonth);
            if (this.monthIncluded(chartMonth, selectedMonth)) {
              const isLastPoint = pointIdx === data.length - 1;
              // Spline markers use white fill, Line markers use series color
              const fillColor = chartType === 'Spline' ? '#fff' : seriesColor;
              return {
                value: d.yData,
                symbolSize: isLastPoint ? 14 : 10,
                itemStyle: {
                  color: fillColor,
                  borderColor: seriesColor,
                  borderWidth: 3,
                  opacity: 1
                }
              };
            }
            // Non-current month points: hide marker
            return {
              value: d.yData,
              symbolSize: 0
            };
          }
          // Column charts: highlight the current-month (August) bar in grey
          if (chartType === 'Column') {
            const { chartMonth, selectedMonth } = this.monthVars(d.xData || '', currentMonth);
            if (this.monthIncluded(chartMonth, selectedMonth)) {
              return {
                value: d.yData,
                itemStyle: {
                  color: '#727476'
                }
              };
            }
          }
          return d.yData;
        });

        const seriesConfig: any = {
          name: this.getSeriesDisplayName(chart.dataTypes[idx]),
          data: seriesData,
          type: this.getEchartsType(chartType),
          smooth: chartType === 'Spline',
          showSymbol: true,
          symbol: isLineType ? 'circle' : 'none',
          yAxisIndex: chart.chartTypes.includes('Line') && idx > 0 ? 1 : 0,
          itemStyle: {
            color: seriesColor
          },
          lineStyle: {
            width: chartType === 'Line' || chartType === 'Spline' || chartType === 'ColumnLine' ? 4 : 0,
            color: seriesColor
          },
          emphasis: {
            itemStyle: {
              borderWidth: 4,
              borderColor: '#fff'
            }
          }
        };

        // Add data labels for columns
        if (chartType === 'Column' && showColumnMarkers) {
          const dataType = chart.dataTypes[idx];
          // Inventory & MSI ('ai') and New Listings ('nl') show a count on every bar
          const showAllBarCounts = dataType === 'ai' || dataType === 'nl';
          seriesConfig.label = {
            show: true,
            position: 'insideTop',
            rotate: 90,
            distance: 5,
            formatter: (params: any) => {
              const value = params.value;
              if (value > 0) {
                if (showAllBarCounts) {
                  return decimalPipe.transform(value);
                }
                const { chartMonth, selectedMonth } = this.monthVars(chartData[idx][params.dataIndex]?.xData || '', currentMonth);
                if (this.monthIncluded(chartMonth, selectedMonth)) {
                  return decimalPipe.transform(value);
                }
              }
              return '';
            },
            color: (params: any) => {
              // White text for the current-month (grey) bar, dark blue otherwise
              const { chartMonth, selectedMonth } = this.monthVars(chartData[idx][params.dataIndex]?.xData || '', currentMonth);
              return this.monthIncluded(chartMonth, selectedMonth) ? '#fff' : '#1F69FF';
            },
            align: 'center',
            verticalAlign: 'middle',
            fontFamily: 'Roboto',
            fontSize: 11
          };
        }

        series.push(seriesConfig);
      });
    }

    // Build x-axis categories
    const xAxisData = chartData && chartData.length > 0 ? chartData[0].map(d => d.xData) : [];

    // Format x-axis labels - show year underneath the month when the year changes
    let trackedYear = 0;
    const formattedXAxisData = xAxisData.map(label => {
      const dateArr = label.split(' ');
      const month = dateArr[0].substring(0, 1);
      const year = dateArr[1];

      if (isMobile) {
        const { chartMonth, selectedMonth } = this.monthVars(label, currentMonth);
        if (this.monthIncluded(chartMonth, selectedMonth)) {
          return `${month}\n${year}`;
        }
        return '';
      } else {
        // Show the year below the month letter whenever the year changes
        const parsedYear = parseInt(year, 10);
        if (parsedYear !== trackedYear) {
          trackedYear = parsedYear;
          return `${month}\n${year}`;
        }
        return `${month}`;
      }
    });

    const primaryValues: number[] = [];
    const secondaryValues: number[] = [];
    (chartData || []).forEach((data, idx) => {
      const onSecondaryAxis = chart.chartTypes.includes('Line') && idx > 0;
      data.forEach(d => {
        if (typeof d.yData === 'number') {
          (onSecondaryAxis ? secondaryValues : primaryValues).push(d.yData);
        }
      });
    });
    const hasLegend = chart.chartTypes.length > 1;
    const primaryMode = chart.chartTypes.includes('Column') ? 'column' : chart.chartTypes.includes('Spline') ? 'spline' : 'normal';
    const primaryScale = this.getSyncfusionYAxisScale(primaryValues, primaryMode, hasLegend, chart.shortHand === 'amsp');
    const secondaryScale = this.getSyncfusionYAxisScale(secondaryValues, 'normal', hasLegend);

    return {
      color: palette,
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#727476',
        borderColor: '#727476',
        textStyle: {
          color: '#fff',
          fontFamily: 'Roboto',
          fontSize: 12
        },
        formatter: (params: any) => {
          if (!params || !params.length) return '';
          const dataIndex = params[0].dataIndex;
          const xValue = xAxisData[dataIndex];

          let tooltip = `<div style="padding: 8px; color: #fff;"><b>${xValue}</b><br/>`;
          params.forEach((param: any) => {
            const value = decimalPipe.transform(param.value);
            tooltip += `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:9px;height:9px;background-color:${param.color};"></span><span style="color: #fff;">${param.seriesName}: ${value}</span><br/>`;
          });
          tooltip += '</div>';
          return tooltip;
        },
        confine: true
      },
      legend: {
        show: chart.chartTypes.length > 1,
        bottom: 0,
        textStyle: {
          fontFamily: 'Roboto Condensed, Arial, sans-serif',
          fontSize: 12,
          color: textColor
        },
        itemGap: 20,
        data: chart.dataTypes.map(dt => this.getSeriesDisplayName(dt))
      },
      grid: {
        left: '15px',
        right: '30px',
        top: '20px',
        bottom: chart.chartTypes.length > 1 ? '50px' : '30px',
        containLabel: true
      },
      xAxis: [
        {
          type: 'category',
          data: formattedXAxisData,
          gridIndex: 0,
          axisLine: {
            lineStyle: {
              color: '#E9E9E9',
              width: 1
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            fontFamily: 'Roboto Condensed, Arial, sans-serif',
            fontSize: 12,
            color: textColor,
            interval: 0
          },
          splitLine: {
            show: false
          }
        }
      ],
      yAxis: [
        {
          type: 'value',
          gridIndex: 0,
          scale: false,
          ...(primaryScale ?? {}),
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            fontFamily: 'Roboto Condensed, Arial, sans-serif',
            fontSize: 12,
            color: textColor,
            formatter: (value: any) => decimalPipe.transform(value)
          },
          splitLine: {
            lineStyle: {
              color: '#E9E9E9',
              width: 1
            }
          }
        },
        ...(chart.chartTypes.includes('Line')
          ? [
              {
                type: 'value',
                gridIndex: 0,
                position: 'right',
                ...(secondaryScale ?? {}),
                axisLine: {
                  show: false
                },
                axisTick: {
                  show: false
                },
                axisLabel: {
                  fontFamily: 'Roboto Condensed, Arial, sans-serif',
                  fontSize: 12,
                  color: textColor,
                  formatter: (value: any) => decimalPipe.transform(value)
                },
                splitLine: {
                  show: false
                }
              }
            ]
          : [])
      ],
      series: series
    };
  }

  // Reproduces Syncfusion's auto Y range: ~3% of plot height as the desired interval count (tuned to the production charts), nice steps
  // of 1/2/5/10, and 'Normal' padding (data range / 20 added, then rounded to the interval; zero baseline when min < 5/6 of max).
  getSyncfusionYAxisScale(
    values: number[],
    mode: 'column' | 'spline' | 'normal',
    hasLegend: boolean,
    extraTopInterval = false
  ): { min: number; max: number; interval: number } | null {
    if (!values.length) {
      return null;
    }
    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);
    if (dataMax <= 0 || dataMin < 0) {
      return null;
    }
    const delta = dataMax - (mode === 'column' ? 0 : dataMin);
    if (delta <= 0) {
      return null;
    }
    const desiredCount = hasLegend ? 9.3 : 10.2;
    let interval = delta / desiredCount;
    const baseInterval = Math.pow(10, Math.floor(Math.log10(interval)));
    for (const div of [10, 5, 2, 1]) {
      const current = baseInterval * div;
      if (desiredCount < delta / current) {
        break;
      }
      interval = current;
    }
    const pad = (dataMax - dataMin) / 20;
    const min = mode === 'column' || dataMin < (dataMax * 5) / 6 ? 0 : Math.floor((dataMin - pad) / interval) * interval;
    const max = Math.ceil((mode === 'spline' ? dataMax : dataMax + pad) / interval) * interval + (extraTopInterval ? interval : 0);
    const clean = (v: number) => parseFloat(v.toPrecision(12));
    return { min: clean(min), max: clean(max), interval: clean(interval) };
  }

  private getEchartsType(chartType: TrendsChartStyles): string {
    switch (chartType) {
      case 'Spline':
        return 'line';
      case 'Line':
        return 'line';
      case 'Column':
        return 'bar';
      case 'StackingColumn':
        return 'bar';
      case 'ColumnLine':
        return 'bar';
      case 'Custom':
        return 'line';
      default:
        return 'line';
    }
  }
}
